import { ColumnDef } from "@tanstack/react-table"
import { ChevronDown, ChevronRight, FileText, Folder, Pin } from "lucide-react"

import { TableRow } from "./types"
import { StudentAssignmentItem } from "../../app/controllers/student-assignments-list/student-assignment-folder-item"

interface ColumnOptions {
  onAssignmentClick: (item: StudentAssignmentItem) => void
}

export function createColumns({
  onAssignmentClick,
}: ColumnOptions): ColumnDef<TableRow>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: ({ table, column }) => (
        <button
          className="tw-flex tw-items-center tw-gap-1 hover:tw-text-foreground"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <button
            className="tw-p-1 hover:tw-bg-gray-100 tw-rounded tw-mr-2"
            onClick={(e) => {
              e.stopPropagation()
              table.toggleAllRowsExpanded()
            }}
            aria-label={
              table.getIsAllRowsExpanded() ? "Collapse all" : "Expand all"
            }
          >
            {table.getIsAllRowsExpanded() ? (
              <ChevronDown className="tw-w-4 tw-h-4" />
            ) : (
              <ChevronRight className="tw-w-4 tw-h-4" />
            )}
          </button>
          Assignment Name
          <span className="tw-font-mono tw-whitespace-pre tw-text-xs">
            {column.getIsSorted() === "asc"
              ? " \u25B2"
              : column.getIsSorted() === "desc"
                ? " \u25BC"
                : "  "}
          </span>
        </button>
      ),
      cell: ({ row, getValue }) => {
        const rowData = row.original
        const isFolder = rowData.type === "folder"
        const isInactiveHeader = rowData.type === "inactive-header"
        const isAssignment = rowData.type === "assignment"
        const item = rowData.assignmentItem

        return (
          <div
            className="tw-flex tw-items-center tw-gap-2"
            style={{ paddingLeft: `${row.depth * 1.5}rem` }}
          >
            {/* Expand/collapse button for folders and inactive headers */}
            {row.getCanExpand() ? (
              <button
                className="tw-p-1 hover:tw-bg-gray-100 tw-rounded"
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
                aria-label={row.getIsExpanded() ? "Collapse" : "Expand"}
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="tw-w-4 tw-h-4" />
                ) : (
                  <ChevronRight className="tw-w-4 tw-h-4" />
                )}
              </button>
            ) : (
              // Spacer for alignment when no expand button
              <div className="tw-w-6" />
            )}

            {/* New work indicator for assignments */}
            {isAssignment && item && !item.isWork && (
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-500 tw-flex-shrink-0" />
            )}

            {/* Icon based on row type */}
            {isFolder && (
              <Folder className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
            )}
            {isInactiveHeader && (
              <Folder className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
            )}
            {isAssignment && item?.isPromoted && (
              <Pin className="tw-w-4 tw-h-4 tw-text-blue-500 tw-flex-shrink-0" />
            )}
            {isAssignment && item && !item.isPromoted && (
              <FileText className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
            )}

            {/* Row name */}
            <span
              className={
                isAssignment
                  ? "tw-cursor-pointer hover:tw-underline"
                  : "tw-font-medium"
              }
              onClick={
                isAssignment && item
                  ? (e) => {
                      e.stopPropagation()
                      onAssignmentClick(item)
                    }
                  : undefined
              }
            >
              {getValue<string>()}
            </span>
          </div>
        )
      },
      enableSorting: true,
    },
    {
      id: "feedback",
      header: ({ column }) => (
        <button
          className="tw-flex tw-items-center tw-gap-1 hover:tw-text-foreground"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Feedback
          <span className="tw-font-mono tw-whitespace-pre tw-text-xs">
            {column.getIsSorted() === "asc"
              ? " \u25B2"
              : column.getIsSorted() === "desc"
                ? " \u25BC"
                : "  "}
          </span>
        </button>
      ),
      accessorFn: (row) => {
        if (row.type === "assignment" && row.assignmentItem) {
          return row.assignmentItem.feedbackCount || 0
        }
        if (row.type === "folder" && row.folder) {
          return row.folder.activeFolder.assignments.reduce(
            (sum, item) => sum + (item.feedbackCount || 0),
            0,
          )
        }
        return 0
      },
      cell: ({ row }) => {
        const item = row.original.assignmentItem
        if (
          row.original.type === "assignment" &&
          item?.isWork &&
          item.feedbackCount !== undefined &&
          item.feedbackCount > 0
        ) {
          return (
            <span className="tw-text-sm tw-text-gray-500">
              {item.feedbackCount}
            </span>
          )
        }
        return null
      },
      enableSorting: true,
    },
    {
      id: "points",
      header: ({ column }) => (
        <button
          className="tw-flex tw-items-center tw-gap-1 hover:tw-text-foreground"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Points
          <span className="tw-font-mono tw-whitespace-pre tw-text-xs">
            {column.getIsSorted() === "asc"
              ? " \u25B2"
              : column.getIsSorted() === "desc"
                ? " \u25BC"
                : "  "}
          </span>
        </button>
      ),
      accessorFn: (row) => {
        if (row.type === "assignment" && row.assignmentItem) {
          const score = row.assignmentItem.studentTotalScore
          return typeof score === "number" ? score : 0
        }
        if (row.type === "folder" && row.folder) {
          return row.folder.activeFolder.assignments.reduce(
            (sum, item) =>
              sum +
              (typeof item.studentTotalScore === "number"
                ? item.studentTotalScore
                : 0),
            0,
          )
        }
        return 0
      },
      cell: ({ row }) => {
        const item = row.original.assignmentItem
        if (
          row.original.type !== "assignment" ||
          !item?.isWork ||
          !item.showStudentScores
        ) {
          return null
        }

        const gradeIsAboveNinety =
          typeof item.studentTotalScore === "number" &&
          item.totalPotentialPoints &&
          (item.studentTotalScore / item.totalPotentialPoints) * 100 >= 90

        return (
          <div className="tw-flex tw-items-center tw-gap-2">
            {item.studentTotalScoreDisplay && (
              <span className="tw-text-sm tw-font-medium">
                {item.studentTotalScoreDisplay}
              </span>
            )}
            {gradeIsAboveNinety && (
              <span className="tw-text-yellow-500 tw-text-sm">
                &star; Nice!
              </span>
            )}
          </div>
        )
      },
      enableSorting: true,
    },
  ]
}
